import { useEffect, useRef, useState } from 'react';
import { Loader2, Wifi } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CaptiveOfferDto, CaptiveSlot, CaptiveTexts, TextSize, TextWeight } from '@/api/captive';
import { assetUrl } from '@/api/captive';

/** Гостевой портал: телефон → код → «доступ открыт» с каруселью.
 *  Один рендер и для превью в настройках, и для живой страницы. */

const SIZE_PX: Record<TextSize, number> = { sm: 13, md: 16, lg: 21, xl: 28 };

const DEFAULTS: Record<CaptiveSlot, { text: string; size: TextSize; weight: TextWeight }> = {
  title: { text: '', size: 'xl', weight: 'bold' },
  subtitle: { text: 'Гостевой Wi-Fi', size: 'md', weight: 'normal' },
  phone_prompt: { text: 'Введите номер телефона', size: 'md', weight: 'normal' },
  legal: { text: 'Номер нужен по требованию закона о публичных сетях. Ничего больше мы не сохраняем.', size: 'sm', weight: 'thin' },
  code_prompt: { text: 'Введите код из СМС', size: 'md', weight: 'normal' },
  success_title: { text: 'Интернет включён', size: 'lg', weight: 'bold' },
  success_text: { text: 'Хорошего дня и вкусного кофе', size: 'md', weight: 'normal' },
  button: { text: 'Продолжить', size: 'md', weight: 'normal' },
};

export function slotOf(texts: CaptiveTexts, slot: CaptiveSlot) {
  const t = texts[slot];
  return { ...DEFAULTS[slot], ...(t || {}) };
}

function weightClass(w: TextWeight) {
  return w === 'bold' ? 'font-semibold' : w === 'thin' ? 'font-light' : w === 'caps' ? 'uppercase tracking-[0.12em]' : 'font-normal';
}

const IDENTITY = {
  linza: {
    page: 'bg-[#f3f6f4] text-[#12201a]',
    card: 'bg-white rounded-3xl border border-black/5 shadow-sm',
    button: 'bg-[#14804a] text-white hover:bg-[#106b3d] rounded-2xl',
    input: 'rounded-2xl border border-black/10 bg-[#f8faf9] focus:border-[#14804a]',
    logo: '/brand/linza-robot.jpg',
    logoClass: 'size-14 rounded-full object-cover',
    muted: 'text-[#5b6b62]',
  },
  surf: {
    page: 'bg-white text-black',
    card: 'bg-white rounded-none border-y border-black',
    button: 'bg-black text-white hover:bg-black/85 rounded-full uppercase tracking-[0.12em]',
    input: 'rounded-none border border-black bg-white focus:border-black',
    logo: '/brand/surf-coffee.png',
    logoClass: 'h-16 w-auto',
    muted: 'text-black/60',
  },
};

export interface CaptivePortalViewProps {
  identity: 'linza' | 'surf';
  branchName: string;
  texts: CaptiveTexts;
  offers: CaptiveOfferDto[];
  onRequestCode?: (phone: string) => Promise<void>;
  onVerify?: (phone: string, code: string) => Promise<void>;
  /** Превью в настройках: сразу показать нужный шаг. */
  forceStep?: 'phone' | 'code' | 'done';
  /** Куда гость шёл до перехвата — туда и вернёт кнопка «продолжить». */
  continueUrl?: string;
}

export function CaptivePortalView({
  identity, branchName, texts, offers, onRequestCode, onVerify, forceStep, continueUrl,
}: CaptivePortalViewProps) {
  const id = IDENTITY[identity] ?? IDENTITY.linza;
  const [step, setStep] = useState<'phone' | 'code' | 'done'>(forceStep ?? 'phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (forceStep) setStep(forceStep); }, [forceStep]);

  const T = (slot: CaptiveSlot) => {
    const s = slotOf(texts, slot);
    return { text: s.text, style: { fontSize: SIZE_PX[s.size] }, cls: weightClass(s.weight) };
  };

  async function submitPhone() {
    setError(null);
    if (phone.replace(/\D/g, '').length < 10) { setError('Введите номер полностью'); return; }
    if (!onRequestCode) { setStep('code'); return; }
    setBusy(true);
    try { await onRequestCode(phone); setStep('code'); }
    catch (e: unknown) { setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Не получилось, попробуйте ещё раз'); }
    finally { setBusy(false); }
  }
  async function submitCode() {
    setError(null);
    if (!onVerify) { setStep('done'); return; }
    setBusy(true);
    try { await onVerify(phone, code); setStep('done'); }
    catch (e: unknown) { setError((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Код неверный'); }
    finally { setBusy(false); }
  }

  const title = T('title');
  const subtitle = T('subtitle');
  return (
    <div className={cn('flex min-h-full flex-col items-center px-5 py-8', id.page)}>
      <img src={id.logo} alt="" className={cn('mb-4', id.logoClass)} />
      <div className={cn('w-full max-w-sm px-5 py-6', id.card)}>
        <h1 className={cn('text-center leading-tight', title.cls)} style={title.style}>{title.text || branchName}</h1>
        {subtitle.text && <p className={cn('mt-1 text-center', id.muted, subtitle.cls)} style={subtitle.style}>{subtitle.text}</p>}

        {step === 'phone' && (
          <div className="mt-6 space-y-3">
            <p className={cn(T('phone_prompt').cls)} style={T('phone_prompt').style}>{T('phone_prompt').text}</p>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
              placeholder="+7 900 000-00-00"
              className={cn('w-full px-3 py-2.5 text-[16px] outline-none', id.input)}
            />
            <p className={cn(id.muted, T('legal').cls)} style={T('legal').style}>{T('legal').text}</p>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="button" onClick={() => void submitPhone()} disabled={busy}
              className={cn('flex h-12 w-full items-center justify-center gap-2 font-medium disabled:opacity-60', id.button, T('button').cls)}>
              {busy && <Loader2 className="size-4 animate-spin" />} Получить код
            </button>
          </div>
        )}

        {step === 'code' && (
          <div className="mt-6 space-y-3">
            <p className={cn(T('code_prompt').cls)} style={T('code_prompt').style}>{T('code_prompt').text}</p>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputMode="numeric"
              placeholder="0000"
              className={cn('w-full px-3 py-2.5 text-center text-[22px] tracking-[0.4em] outline-none', id.input)}
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="button" onClick={() => void submitCode()} disabled={busy}
              className={cn('flex h-12 w-full items-center justify-center gap-2 font-medium disabled:opacity-60', id.button, T('button').cls)}>
              {busy && <Loader2 className="size-4 animate-spin" />} Подключиться
            </button>
          </div>
        )}

        {step === 'done' && (
          <div className="mt-6 space-y-4 text-center">
            <Wifi className="mx-auto size-8" />
            <p className={cn(T('success_title').cls)} style={T('success_title').style}>{T('success_title').text}</p>
            <p className={cn(id.muted, T('success_text').cls)} style={T('success_text').style}>{T('success_text').text}</p>
            {offers.length > 0 && <OfferCarousel offers={offers} />}
            <button type="button"
              onClick={() => { if (continueUrl) window.location.assign(continueUrl); }}
              className={cn('flex h-12 w-full items-center justify-center font-medium', id.button, T('button').cls)}>
              {T('button').text}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Спецпредложения: сами листаются раз в 5 секунд, свайпом — тоже. */
function OfferCarousel({ offers }: { offers: CaptiveOfferDto[] }) {
  const [i, setI] = useState(0);
  const touchX = useRef<number | null>(null);
  useEffect(() => {
    if (offers.length < 2) return;
    const id = window.setInterval(() => setI((p) => (p + 1) % offers.length), 5000);
    return () => window.clearInterval(id);
  }, [offers.length]);
  const go = (next: number) => setI(((next % offers.length) + offers.length) % offers.length);
  return (
    <div
      className="overflow-hidden rounded-xl"
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) go(i + (dx < 0 ? 1 : -1));
        touchX.current = null;
      }}
    >
      <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${i * 100}%)` }}>
        {offers.map((o) => (
          <figure key={o.id} className="w-full shrink-0">
            <img src={assetUrl(o.url)} alt={o.caption} className="h-44 w-full object-cover" />
            {o.caption && <figcaption className="px-2 py-1 text-xs text-muted-foreground">{o.caption}</figcaption>}
          </figure>
        ))}
      </div>
      {offers.length > 1 && (
        <div className="mt-2 flex justify-center gap-1.5">
          {offers.map((o, n) => (
            <button key={o.id} type="button" aria-label={`Слайд ${n + 1}`} onClick={() => go(n)}
              className={cn('h-1.5 rounded-full transition-all', n === i ? 'w-4 bg-current' : 'w-1.5 bg-current/30')} />
          ))}
        </div>
      )}
    </div>
  );
}
